import type { AddressPageConfig, LayoutConfig, PageLayoutVersion } from '@tenants/types';

/**
 * Options for creating address page configuration
 */
export interface AddressPageConfigOptions {
  /** 
   * Page layout version (v1 = legacy, v2 = redesign)
   * 
   * Can be a single version or separate mobile/desktop:
   * - `'v2'` - same layout for both viewports
   * - `{ mobile: 'v1', desktop: 'v2' }` - different layouts per viewport
   * 
   * @default 'v1'
   */
  layout?: LayoutConfig;
}

/**
 * Helper to get layout for a specific viewport
 * 
 * @param layout - Layout config (string or mobile/desktop object)
 * @param viewport - Target viewport
 * @returns The layout version for the specified viewport
 */
export function getLayoutForViewport(
  layout: LayoutConfig,
  viewport: 'mobile' | 'desktop'
): PageLayoutVersion {
  if (typeof layout === 'string') {
    return layout;
  }
  return layout[viewport];
}

/**
 * Creates address page configuration with consistent structure across all tenants
 * 
 * Layout configuration:
 * - v1: Legacy layout (carousel hero, two-column content)
 * - v2: Redesign (bento gallery, sticky sidebar, new header, sticky footer)
 * 
 * Supports separate mobile/desktop layouts for A/B testing:
 * - Single: `{ layout: 'v2' }` - same for both viewports
 * - Split: `{ layout: { mobile: 'v1', desktop: 'v2' } }` - different per viewport
 * 
 * Components derive their own visibility from layout version.
 * 
 * @param options - Optional configuration overrides
 * @returns Address page configuration
 * 
 * @example
 * // Default v1 layout
 * createAddressPageConfig()
 * 
 * @example
 * // v2 layout for all viewports
 * createAddressPageConfig({ layout: 'v2' })
 * 
 * @example
 * // Split layout for A/B testing
 * createAddressPageConfig({ layout: { mobile: 'v2', desktop: 'v1' } })
 */
export function createAddressPageConfig(
  options?: AddressPageConfigOptions
): AddressPageConfig {
  return {
    layout: options?.layout ?? 'v1',
  };
}
